/* ═══════════════════════════════════════════
   WIKILOC WIDGET — Embed de trilhas
   Mantém o usuário no site ao invés de
   redirecionar para o Wikiloc externo.
   ═══════════════════════════════════════════ */

import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface WikilocWidgetProps {
  /** ID da trilha no Wikiloc (ex: 99363870) */
  trailId: string;
  /** Largura do embed (default: 100%) */
  width?: string;
  /** Altura do embed em pixels (default: 500) */
  height?: number;
  /** Exibir gráfico de elevação (default: false) */
  elevation?: boolean;
  /** Exibir fotos da trilha (default: false) */
  images?: boolean;
  /** Tipo de mapa: H=hybrid, S=satellite, T=terrain, M=map (default: H) */
  mapType?: 'H' | 'S' | 'T' | 'M';
  /** Título acessível para leitores de tela */
  title?: string;
}

export function WikilocWidget({
  trailId,
  width = '100%',
  height = 500,
  elevation = false,
  images = false,
  mapType = 'H',
  title,
}: WikilocWidgetProps) {
  const { t } = useTranslation();
  const { trackOutbound } = useAnalytics();
  const resolvedTitle = title || t('widget.wikiloc.title');

  const params = new URLSearchParams({
    id: trailId,
    elevation: elevation ? 'on' : 'off',
    images: images ? 'on' : 'off',
    maptype: mapType,
  });

  const src = `https://www.wikiloc.com/wikiloc/embedv2.do?${params.toString()}`;

  return (
    <div className="w-full rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] bg-white">
      <div className="relative w-full" style={{ height }}>
        <iframe
          src={src}
          title={resolvedTitle}
          width={width}
          height={height}
          frameBorder={0}
          scrolling="no"
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          allow="fullscreen"
        />
      </div>
      <div className="px-4 py-2.5 bg-sand-50 border-t border-sand-100 flex items-center justify-between">
        <span className="font-body text-[11px] text-warm-gray">{t('widget.wikiloc.footer')}</span>
        <a
          href={`https://www.wikiloc.com/trilhas/${trailId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackOutbound(`https://www.wikiloc.com/trilhas/${trailId}`, 'Ver detalhes no Wikiloc', 'widget_wikiloc')}
          className="font-body text-[11px] text-ocean hover:underline font-semibold"
        >
          {t('widget.wikiloc.viewDetails')}
        </a>
      </div>
    </div>
  );
}
